/**
 * Rasch Bayesian Adaptive Test utilities (frontend)
 *
 * This module provides:
 * - Stable logistic and log-sigmoid utilities
 * - Rasch likelihood helpers
 * - Log-posterior, first and second derivatives
 * - Newton–Raphson MAP estimation
 * - Laplace variance approximation
 * - Normalization utilities for default_ranking
 * - Probe-card selection helper (nearest normalized difficulty, 5th-card rule)
 *
 * All functions are pure and safe for frontend use. Where appropriate, functions accept a
 * `debug` option to emit detailed console logs of intermediate steps.
 */

/**
 * Constants for normalization
 * Normalize(δ_raw)   = (δ_raw - CENTER) / SCALE
 * DeNormalize(δ_norm) = (δ_norm * SCALE) + CENTER
 */
export const NORMALIZATION = Object.freeze({
  CENTER: 2500,
  SCALE: 500,
});

/**
 * Default parameters for Newton–Raphson MAP search.
 */
export const NEWTON_DEFAULTS = Object.freeze({
  tol: 1e-6,
  maxIter: 10,
});

/**
 * Utility: check if a value is a finite number.
 * @param {any} x
 * @returns {boolean}
 */
export function isFiniteNumber(x) {
  return typeof x === "number" && Number.isFinite(x);
}

/**
 * Stable softplus
 * softplus(x) = log(1 + exp(x))
 * @param {number} x
 * @returns {number}
 */
export function softplus(x) {
  // Avoid overflow/underflow in exp
  if (x > 0) {
    return x + Math.log1p(Math.exp(-x));
  }
  return Math.log1p(Math.exp(x));
}

/**
 * Numerically-stable sigmoid σ(t) = 1 / (1 + e^{-t})
 * @param {number} t
 * @returns {number}
 */
export function sigmoid(t) {
  if (t >= 0) {
    const z = Math.exp(-t);
    return 1 / (1 + z);
  } else {
    const z = Math.exp(t);
    return z / (1 + z);
  }
}

/**
 * log σ(t) = -softplus(-t) (stable)
 * @param {number} t
 * @returns {number}
 */
export function logSigmoid(t) {
  return -softplus(-t);
}

/**
 * log(1 - σ(t)) = -softplus(t) (stable)
 * @param {number} t
 * @returns {number}
 */
export function log1mSigmoid(t) {
  return -softplus(t);
}

/**
 * Normalize a raw default_ranking (δ_raw) to Rasch scale.
 * Normalize(δ_raw) = (δ_raw - CENTER) / SCALE
 * @param {number} raw
 * @param {number} [center=NORMALIZATION.CENTER]
 * @param {number} [scale=NORMALIZATION.SCALE]
 * @returns {number}
 */
export function normalizeRanking(
  raw,
  center = NORMALIZATION.CENTER,
  scale = NORMALIZATION.SCALE
) {
  const r = Number(raw);
  return (r - center) / scale;
}

/**
 * De-normalize δ_norm back to raw default_ranking.
 * DeNormalize(δ_norm) = (δ_norm * SCALE) + CENTER
 * @param {number} norm
 * @param {number} [center=NORMALIZATION.CENTER]
 * @param {number} [scale=NORMALIZATION.SCALE]
 * @returns {number}
 */
export function denormalizeRanking(
  norm,
  center = NORMALIZATION.CENTER,
  scale = NORMALIZATION.SCALE
) {
  const n = Number(norm);
  return n * scale + center;
}

/**
 * Rasch probability of a correct response with ability β and difficulty δ.
 * p(x=1 | β, δ) = σ(β - δ)
 * @param {number} beta
 * @param {number} delta
 * @returns {number}
 */
export function raschProbCorrect(beta, delta) {
  return sigmoid(beta - delta);
}

/**
 * Log-likelihood for a single binary response x ∈ {0,1} under the Rasch model.
 * ℓ_like(β) = x * log σ(β - δ) + (1 - x) * log(1 - σ(β - δ))
 * @param {number} beta
 * @param {number} delta
 * @param {0|1|boolean|number} x
 * @returns {number}
 */
export function logLikelihood(beta, delta, x) {
  const xi = Number(x ? 1 : 0);
  const t = beta - delta;
  return xi * logSigmoid(t) + (1 - xi) * log1mSigmoid(t);
}

/**
 * Log prior (Normal) density up to constant (includes normalization constant by default).
 * log N(β | μ, λ²) = -0.5 * ((β-μ)² / λ²) - 0.5 * log(2π λ²)
 *
 * You may omit constants by passing includeConstant = false.
 *
 * @param {number} beta
 * @param {number} mu
 * @param {number} var_
 * @param {boolean} [includeConstant=true]
 * @returns {number}
 */
export function logPriorNormal(beta, mu, var_, includeConstant = true) {
  const v = Math.max(Number(var_), Number.EPSILON);
  const diff = beta - mu;
  const logBase = (-0.5 * (diff * diff)) / v;
  if (!includeConstant) return logBase;
  return logBase - 0.5 * Math.log(2 * Math.PI * v);
}

/**
 * Unnormalized log-posterior (log-likelihood + log-prior).
 * @param {number} beta
 * @param {number} delta
 * @param {number} mu
 * @param {number} var_
 * @param {0|1|boolean|number} x
 * @returns {number}
 */
export function logPosterior(beta, delta, mu, var_, x) {
  return logLikelihood(beta, delta, x) + logPriorNormal(beta, mu, var_, false);
}

/**
 * First derivative of log-posterior wrt β (stable form).
 * ℓ′(β) = x - σ(β - δ) - (β - μ)/λ²
 * @param {number} beta
 * @param {number} delta
 * @param {number} mu
 * @param {number} var_
 * @param {0|1|boolean|number} x
 * @returns {number}
 */
export function dLogPosterior(beta, delta, mu, var_, x) {
  const xi = Number(x ? 1 : 0);
  const s = sigmoid(beta - delta);
  const v = Math.max(Number(var_), Number.EPSILON);
  return xi - s - (beta - mu) / v;
}

/**
 * Second derivative of log-posterior wrt β (stable form).
 * ℓ″(β) = -σ(β - δ) * (1 - σ(β - δ)) - 1/λ²
 * @param {number} beta
 * @param {number} delta
 * @param {number} mu
 * @param {number} var_
 * @param {0|1|boolean|number} x
 * @returns {number}
 */
export function d2LogPosterior(beta, delta, _mu, var_) {
  const s = sigmoid(beta - delta);
  const v = Math.max(Number(var_), Number.EPSILON);
  return -(s * (1 - s)) - 1 / v;
}

/**
 * Newton–Raphson MAP solver for a single binary response.
 * Starts from prior mean as initial guess unless provided.
 *
 * β_{n+1} = β_n - ℓ′(β_n) / ℓ″(β_n)
 *
 * Stops when |step| < tol or maxIter reached. If a non-finite derivative is encountered
 * or the denominator is ~0, the loop aborts with converged = false.
 *
 * @param {Object} params
 * @param {number} params.mu - prior mean
 * @param {number} params.var - prior variance (λ²)
 * @param {number} params.delta - item difficulty (normalized)
 * @param {0|1|boolean|number} params.x - response
 * @param {number} [params.initial] - optional initial β; defaults to mu
 * @param {number} [params.tol=1e-6]
 * @param {number} [params.maxIter=10]
 * @param {boolean} [params.debug=false]
 * @returns {{ betaHat: number, iterations: number, converged: boolean, path: Array<{beta:number, g:number, h:number, step:number, logPost:number}> }}
 */
export function newtonRaphsonMAP({
  mu,
  var: var_,
  delta,
  x,
  initial,
  tol = NEWTON_DEFAULTS.tol,
  maxIter = NEWTON_DEFAULTS.maxIter,
  debug = false,
}) {
  let beta = isFiniteNumber(initial) ? Number(initial) : Number(mu);
  const path = [];

  if (debug) {
    // eslint-disable-next-line no-console
    console.log("[raschAdaptive] Newton–Raphson start", {
      mu,
      var: var_,
      delta,
      x: Number(x ? 1 : 0),
      initial: beta,
      tol,
      maxIter,
    });
  }

  let converged = false;
  let iterations = 0;

  for (let i = 0; i < maxIter; i++) {
    const g = dLogPosterior(beta, delta, mu, var_, x);
    const h = d2LogPosterior(beta, delta, mu, var_, x);
    const lp = logPosterior(beta, delta, mu, var_, x);

    if (!isFiniteNumber(g) || !isFiniteNumber(h) || h === 0) {
      if (debug) {
        // eslint-disable-next-line no-console
        console.warn(
          "[raschAdaptive] Newton–Raphson abort: invalid derivative",
          {
            i,
            beta,
            g,
            h,
          }
        );
      }
      iterations = i;
      break;
    }

    const step = -g / h;
    const next = beta + step;

    path.push({ beta, g, h, step, logPost: lp });

    if (debug) {
      // eslint-disable-next-line no-console
      console.log("[raschAdaptive] Newton–Raphson iter", {
        i,
        beta,
        g,
        h,
        step,
        next,
        lp,
      });
    }

    iterations = i + 1;

    if (Math.abs(step) < tol) {
      beta = next;
      converged = true;
      break;
    }

    beta = next;
  }

  if (debug) {
    // eslint-disable-next-line no-console
    console.log("[raschAdaptive] Newton–Raphson done", {
      betaHat: beta,
      iterations,
      converged,
      last: path[path.length - 1] || null,
    });
  }

  return { betaHat: beta, iterations, converged, path };
}

/**
 * Laplace variance approximation at MAP:
 * σ²_post = -1 / ℓ″(β̂)
 * If result is not finite or <= 0, fallback to the prior variance.
 *
 * @param {number} betaHat
 * @param {number} delta
 * @param {number} mu
 * @param {number} var_
 * @param {0|1|boolean|number} x
 * @param {boolean} [debug=false]
 * @returns {number}
 */
export function laplaceVariance(betaHat, delta, mu, var_, x, debug = false) {
  const h = d2LogPosterior(betaHat, delta, mu, var_, x);
  let v = -1 / h;

  if (!isFiniteNumber(v) || v <= 0) {
    if (debug) {
      // eslint-disable-next-line no-console
      console.warn(
        "[raschAdaptive] Laplace variance invalid; falling back to prior variance",
        {
          betaHat,
          h,
          computed: v,
          priorVar: var_,
        }
      );
    }
    v = Math.max(Number(var_), Number.EPSILON);
  }

  if (debug) {
    // eslint-disable-next-line no-console
    console.log("[raschAdaptive] Laplace variance", { betaHat, h, var: v });
  }

  return v;
}

/**
 * One-step Bayesian update for a single observed response on a probe item.
 * Uses Newton–Raphson MAP and Laplace variance approximation.
 *
 * @param {Object} params
 * @param {{ mean: number, var: number }} params.prior - {μ, λ²}
 * @param {number} params.deltaRaw - raw default_ranking of the presented card
 * @param {0|1|boolean|number} params.x - observed response (true/1 = correct)
 * @param {number} [params.center=NORMALIZATION.CENTER]
 * @param {number} [params.scale=NORMALIZATION.SCALE]
 * @param {boolean} [params.debug=false]
 * @returns {{ posterior: { mean: number, var: number }, deltaNorm: number, map: ReturnType<typeof newtonRaphsonMAP>, laplaceVar: number, pCorrectBefore: number }}
 */
export function updateAbilityPosterior({
  prior,
  deltaRaw,
  x,
  center = NORMALIZATION.CENTER,
  scale = NORMALIZATION.SCALE,
  debug = false,
}) {
  const mu = Number(prior?.mean ?? 0);
  const var_ = Math.max(Number(prior?.var ?? 1), Number.EPSILON);
  const deltaNorm = normalizeRanking(deltaRaw, center, scale);

  // Probability of correct before observing x (for logging sanity)
  const pCorrectBefore = raschProbCorrect(mu, deltaNorm);

  const map = newtonRaphsonMAP({
    mu,
    var: var_,
    delta: deltaNorm,
    x,
    initial: mu,
    debug,
  });

  // Laplace variance for reference/logging
  const lapVar = laplaceVariance(map.betaHat, deltaNorm, mu, var_, x, debug);

  // Tempered variance update using Fisher information at MAP
  const pAtMAP = raschProbCorrect(map.betaHat, deltaNorm);
  const I = pAtMAP * (1 - pAtMAP); // item information (max 0.25)
  const kappa = 4 / 9; // ≈0.444..., tuned for 35-card test targeting ~0.25 posterior var from prior var≈9 with I≈0.25
  const precisionNew = 1 / var_ + kappa * I;
  let temperedVar = 1 / precisionNew;

  // Stability fallback
  if (!isFiniteNumber(temperedVar) || temperedVar <= 0) {
    temperedVar = Math.max(Number(var_), Number.EPSILON);
  }

  const posterior = { mean: map.betaHat, var: temperedVar };

  if (debug) {
    // eslint-disable-next-line no-console
    console.log("[raschAdaptive] Posterior update", {
      prior: { mean: mu, var: var_ },
      deltaRaw,
      deltaNorm,
      x: Number(x ? 1 : 0),
      pCorrectBefore,
      pAtMAP,
      fisherInformation: I,
      kappa,
      laplaceVar: lapVar,
      temperedVar,
      posterior,
    });
  }

  return { posterior, deltaNorm, map, laplaceVar: lapVar, pCorrectBefore };
}

/**
 * Compute p(correct) at δ = 0 for a given ability mean (μ).
 * Handy for sanity checks:
 * - μ ≈ 0 → p ≈ 0.5
 * - μ = −3 → p ≈ 0.05
 * - μ = +3 → p ≈ 0.95
 *
 * @param {number} mu
 * @returns {number}
 */
export function sanityProbAtZero(mu) {
  return raschProbCorrect(mu, 0);
}

/**
 * Clamp final starting_word_rank based on the posterior mean:
 * rank = round(DeNormalize(μ))
 *
 * @param {number} meanNorm
 * @param {number} [minRank=0]
 * @param {number} [maxRank=5000]
 * @param {number} [center=NORMALIZATION.CENTER]
 * @param {number} [scale=NORMALIZATION.SCALE]
 * @returns {number}
 */
export function computeStartingWordRank(
  meanNorm,
  minRank = 0,
  maxRank = 5000,
  center = NORMALIZATION.CENTER,
  scale = NORMALIZATION.SCALE
) {
  const raw = denormalizeRanking(meanNorm, center, scale);
  const clamped = Math.max(minRank, Math.min(maxRank, Math.round(raw)));
  return clamped;
}

/**
 * Determine if a given card id is eligible as a probe (5th-card rule).
 * @param {number} id
 * @returns {boolean}
 */
export function isProbeEligibleId(id) {
  return Number(id) % 5 === 0;
}

/**
 * Select the next probe card among candidates that obey the 5th-card rule,
 * choosing the one whose normalized difficulty is closest to current mean (μ),
 * skipping any previously used ids.
 *
 * @param {Object} params
 * @param {{ id: number, default_ranking: number }[]} params.cards - candidate cards for the active language
 * @param {number} params.mean - current ability mean (normalized)
 * @param {Set<number>|number[]} [params.usedIds] - ids to skip (already shown in this onboarding test)
 * @param {number} [params.center=NORMALIZATION.CENTER]
 * @param {number} [params.scale=NORMALIZATION.SCALE]
 * @returns {{ id: number, default_ranking: number } | null}
 */
export function selectNextProbeCard({
  cards,
  mean,
  usedIds,
  center = NORMALIZATION.CENTRE ?? NORMALIZATION.CENTER, // tolerate typo if refactored
  scale = NORMALIZATION.SCALE,
}) {
  const used = Array.isArray(usedIds)
    ? new Set(usedIds.map((n) => Number(n)))
    : usedIds instanceof Set
    ? usedIds
    : new Set();

  let best = null;
  let bestDist = Infinity;

  for (const c of Array.isArray(cards) ? cards : []) {
    if (!c || !isFiniteNumber(c.id) || !isFiniteNumber(c.default_ranking))
      continue;
    if (!isProbeEligibleId(c.id)) continue;
    if (used.has(c.id)) continue;

    const dNorm = normalizeRanking(c.default_ranking, center, scale);
    const dist = Math.abs(dNorm - mean);

    if (dist < bestDist) {
      best = c;
      bestDist = dist;
    }
  }

  return best || null;
}

/**
 * Convenience: log an entire update step (selection + posterior) for analytics/debugging.
 * This is optional; use it to instrument your onboarding loop.
 *
 * @param {Object} params
 * @param {{ mean: number, var: number }} params.prior
 * @param {{ id: number, default_ranking: number }} params.card
 * @param {0|1|boolean|number} params.x
 * @param {Object} [params.meta] - additional fields to include in the log
 * @param {boolean} [params.debug=true]
 * @returns {{ posterior: { mean: number, var: number }, deltaNorm: number, map: any, laplaceVar: number, pCorrectBefore: number }}
 */
export function logUpdateStep({ prior, card, x, meta, debug = true }) {
  const res = updateAbilityPosterior({
    prior,
    deltaRaw: card?.default_ranking,
    x,
    debug,
  });

  // eslint-disable-next-line no-console
  if (debug) {
    console.log("[raschAdaptive] Step", {
      meta: meta || null,
      cardId: card?.id,
      default_ranking: card?.default_ranking,
      normalized_delta: res.deltaNorm,
      response: Number(x ? 1 : 0),
      p_correct_before: res.pCorrectBefore,
      prior,
      map_last: res.map?.path?.[res.map?.path?.length - 1] ?? null,
      posterior: res.posterior,
    });
  }

  return res;
}

export default {
  NORMALIZATION,
  NEWTON_DEFAULTS,
  isFiniteNumber,
  softplus,
  sigmoid,
  logSigmoid,
  log1mSigmoid,
  normalizeRanking,
  denormalizeRanking,
  raschProbCorrect,
  logLikelihood,
  logPriorNormal,
  logPosterior,
  dLogPosterior,
  d2LogPosterior,
  newtonRaphsonMAP,
  laplaceVariance,
  updateAbilityPosterior,
  sanityProbAtZero,
  computeStartingWordRank,
  isProbeEligibleId,
  selectNextProbeCard,
  logUpdateStep,
};
